import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { PhoneCodeInputProps } from '../../types/formType';

export const PhoneCodeInput: React.FC<PhoneCodeInputProps> = ({
  value,
  onChange,
  onValidationChange,
  onComplete,
  onEnter,
  disabled = false,
  className = "",
  placeholder = "0",
  autoFocus = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-disabled': ariaDisabled,
  'aria-autocomplete': ariaAutocomplete,
  'aria-controls': ariaControls,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Synchronizes state with external value
  useEffect(() => {
    if (value !== digits.join('')) {
      const newDigits = Array(6).fill('');
      for (let i = 0; i < Math.min(value.length, 6); i++) {
        newDigits[i] = value[i];
      }
      setDigits(newDigits);
    }
  }, [value, digits]);

  // Automatic validation
  useEffect(() => {
    const code = digits.join('');
    const isValid = code.length === 6 && /^\d{6}$/.test(code);
    onValidationChange?.(isValid);
  }, [digits, onValidationChange]);

  const handleDigitChange = useCallback((index: number, newValue: string) => {
    // Only allows one numeric character
    if (newValue.length > 1) newValue = newValue.slice(-1);
    if (newValue && !/^\d$/.test(newValue)) return;

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);
    onChange(newDigits.join(''));

    // If something was typed, focus the next field
    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newValue && index === 5) {
      queueMicrotask(() => onComplete?.());
    }
  }, [digits, onChange, onComplete]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const newDigits = [...digits];

    // BACKSPACE
    if (e.key === 'Backspace') {
      e.preventDefault();

      if (newDigits[index]) {
        // Erase the current one
        newDigits[index] = '';
        setDigits(newDigits);
        onChange(newDigits.join(''));
      } else if (index > 0) {
        // Move to the previous one
        inputRefs.current[index - 1]?.focus();
      }
    }

    // DELETE
    if (e.key === 'Delete') {
      e.preventDefault();
      newDigits[index] = '';
      setDigits(newDigits);
      onChange(newDigits.join(''));
    }

    // ARROWS — always work
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }

    if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === 6 && /^\d{6}$/.test(code)) {
        e.preventDefault();
        onEnter?.();
      }
    }
  }, [digits, onChange, onEnter]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length > 0) {
      const newDigits = Array(6).fill('');
      for (let i = 0; i < pastedData.length; i++) {
        newDigits[i] = pastedData[i];
      }
      setDigits(newDigits);
      onChange(pastedData);

      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();

      if (pastedData.length === 6) {
        queueMicrotask(() => onComplete?.());
      }
    }
  }, [onChange, onComplete]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const t = window.setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [autoFocus, disabled]);

  // Automatically selects the digit when the field is focused
  const handleFocus = useCallback((index: number) => {
    const input = inputRefs.current[index];
    if (!input) return;

    // If there is already a value, select it — if not, just position the cursor
    if (digits[index]) {
      requestAnimationFrame(() => {
        input.setSelectionRange(0, input.value.length);
      });
    }
  }, [digits]);

  return (
    <div
      className={`bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-gap-8 ${className}`}
      role="group"
      aria-label={ariaLabel || "Código de verificação"}
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleDigitChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-10 h-10 mr-2 bsds-text-center text-base font-normal border-1 border-gray-100/50 rounded-sm focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-none transition-colors placeholder:font-semibold`.trim()}
          aria-label={ariaLabel ? `${ariaLabel} - dígito ${index + 1}` : `Dígito ${index + 1} do código de verificação`}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          aria-disabled={ariaDisabled}
          aria-autocomplete={ariaAutocomplete}
          aria-controls={ariaControls}
          aria-errormessage={ariaErrorMessage}
        />
      ))}
    </div>
  );
};
